import { Fragment, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import {
  FormDropDownList,
  FormDropDownListById,
  FormInput,
  FormRadioGroup,
  FormTextArea,
} from "../../../forms/form-components";
import { FormRegion } from "../../../components/formregion";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";

import {
  loadDatasets,
  setFormValue,
  showHideGoogleSheets,
} from "../../../redux/slices/scheduleviewpageslice";
import { useTranslation } from "react-i18next";
import QueryBuilder from "react-querybuilder";
import { getQueryOperators } from "../../../components/querybuilder/queryoperators";
import { QueryValueEditor } from "../../../components/querybuilder/queryvalueeditor";
import { CollapsableFormRegion } from "../../../components/collapsableformregion";
import { ReQuartzCron } from "@sbzen/re-cron";
import { getWidgetKeyFromType } from "../../../components/widgets/utils";

export const ScheduleViewPageForm = (props) => {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state) => state.currentTheme);
  const pageState = useSelector((state) => state.scheduleViewPage);
  const currentDataset = pageState.currentDataset;
  const updateDirtyState = props.updateDirtyState;
  const [tabSelected, setTabSelected] = useState(0);
  const { t } = useTranslation();
  const allowedTypes = [2, 4, 5];

  const listOfIdentityColumn = pageState.currentDataset?.Fields?.filter(
    (field) => allowedTypes.includes(field.DataTypeId),
  );

  const lstOfDatasets = pageState.formValues.Datasets ?? [];

  const ddlType = [
    //{ Id: 1, Title: "Pdf" },
    { Id: 2, Title: t("schedule.excel") },
  ];

  const ddlSource = [
    { Id: 1, Title: t("schedule.google_sheets") },
    // { Id: 2, Title: "Https" },
    // { Id: 3, Title: "SFTP" },
    // { Id: 4, Title: "Email" },
  ];

  const ddlRecurrsEvery = [
    { Id: 0, Title: t("slices.none") },
    { Id: 5, Title: t("slices.fiveMins") },
    { Id: 10, Title: t("slices.tenMins") },
    { Id: 15, Title: t("slices.fiveteenMins") },
    { Id: 30, Title: t("slices.thirtyMins") },
    { Id: 60, Title: t("slices.oneHour") },
    { Id: 120, Title: t("slices.twoHours") },
    { Id: 180, Title: t("slices.threeHours") },
    { Id: 240, Title: t("slices.fourHours") },
    { Id: 300, Title: t("slices.fiveHours") },
    { Id: 360, Title: t("slices.sixHours") },
    { Id: 720, Title: t("slices.twelveHours") },
    { Id: 1440, Title: t("slices.oneDay") },
    { Id: 2880, Title: t("slices.twoDays") },
    { Id: 4320, Title: t("slices.threeDays") },
    { Id: 10080, Title: t("slices.sevenDays") },
    { Id: 43200, Title: t("slices.thirtyDays") },
  ];

  const getOperators = (fieldName) => {
    let field = currentDataset.Where[fieldName];
    if (!field) return [];

    return getQueryOperators(field.DataTypeId, field.IsLookup);
  };

  const handleTabSelection = (e) => {
    setTabSelected(e.selected);
  };

  //handle is dirtyyy
  useEffect(() => {
    updateDirtyState();
  }, [pageState.formInitialValues, pageState.formValues]);

  const listOfFiles =
    pageState.formValues.GoogleList?.filter((f) => f.IsSheet) ?? [];

  useEffect(() => {
    if (pageState.formValues.importexport == "import") props.loadDatasets(t);
  }, [pageState.formValues.DatasetId, pageState.formValues.importexport]);

  useEffect(() => {
    const source = pageState.formValues.SourceId;
    dispatch(showHideGoogleSheets(source === 1 || source === "1"));
    if (source === 1 || source === "1") {
      props.loadGoogleConnections(t);
    }
  }, [pageState.formValues.SourceId]);

  useEffect(() => {
    if (pageState.formValues.ConnectorConfigId) {
      props.loadGoogleConnectorSheets(t);
    }
  }, [pageState.formValues.ConnectorConfigId]);

  useEffect(() => {
    if (
      pageState.formValues.GoogleList?.length > 0 &&
      pageState.formValues.SheetName
    ) {
      props.getGoogleSheetId(
        pageState.formValues.FileId,
        pageState.formValues.SheetName,
        pageState.formValues.FileName,
        pageState.formValues.ConnectorConfigId,
        t,
      );
    }
  }, [pageState.formValues.GoogleList?.length, pageState.formValues.SheetName]);

  useEffect(() => {
    const widgets = pageState.formValues.Widgets;
    let widgetsIds = pageState.formValues.WidgetsIds;

    if (!widgets || widgets.length === 0 || !widgetsIds) return;

    try {
      // Normalize WidgetsIds
      if (typeof widgetsIds === "string") {
        widgetsIds = JSON.parse(widgetsIds);
      }

      const widgetId = widgetsIds?.[0];
      if (!widgetId) return;

      const widget = widgets.find((w) => w.Id == widgetId);
      if (!widget) return;

      const title = widget.Settings?.Title?.trim() || t("schedule.notitle");

      const widgetKey = getWidgetKeyFromType(widget.WidgetTypeId) ?? "Widget";

      const val = `${widgetKey} - ${title}`;

      props.onChangeFormValue("WidgetTitle", val);
    } catch (err) {
      console.warn("WidgetTitle parse error:", err);
    }
  }, [pageState.formValues.WidgetsIds, pageState.formValues.Widgets, t]);
  //#endregion

  const CronOptionLabel = () => {
    return (
      <span className="cron-option-label">
        <span>{t("schedule.selectcron")}</span>

        <span
          className="cron-help-icon"
          title={t("schedule.learnmore")}
          onClick={(e) => {
            e.stopPropagation(); // prevent radio toggle
            window.open("https://recron.emptyui.com/", "_blank");
          }}
        >
          ?
        </span>
      </span>
    );
  };

  const selectFromList = [
    { label: t("schedule.selecttime"), value: "fromRecurrency" },
    {
      label: <CronOptionLabel />,
      value: "fromCron",
    },
  ];

  const customFields = [...(pageState.formValues.CustomFields ?? [])];

  const updateCustomField = (index, key, value) => {
    const updated = customFields.map((item, i) =>
      i === index ? { ...item, [key]: value } : item,
    );

    props.onChangeFormValue("CustomFields", updated, props.onChange);
  };

  const addCustomField = () => {
    const updated = [
      ...customFields,
      { index: customFields.length + 1, field: "", value: "" },
    ];
    props.onChangeFormValue("CustomFields", updated, props.onChange);
  };

  const removeCustomField = (indexToRemove) => {
    const updated = customFields
      .filter((_, i) => i !== indexToRemove)
      .map((item, i) => ({ ...item, index: i + 1 }));
    props.onChangeFormValue("CustomFields", updated, props.onChange);
  };

  return (
    <Fragment>
      <Form
        //validator={formValidator(pageState.mode)}
        key={pageState.formKey}
        initialValues={{ ...pageState.formValues }}
        render={(formRenderProps) => {
          return (
            <FormElement horizontal={false}>
              <FormRegion className="form-region-1">
                <div className="form-group form-group-1">
                  <TabStrip
                    selected={tabSelected}
                    onSelect={handleTabSelection}
                  >
                    <TabStripTab title={t("datasets.datasetview.info")}>
                      <div className="dataset-info">
                        <CollapsableFormRegion
                          className="form-region-main"
                          title={t("alerts.general")}
                          open={pageState.formValues.generalOpen}
                          onToggle={(e) =>
                            dispatch(
                              setFormValue({
                                id: "generalOpen",
                                value: !pageState.formValues.generalOpen,
                              }),
                            )
                          }
                        >
                          {pageState.formValues.importexport == "export" && (
                            <>
                              <Field
                                id={"ReportTitle"}
                                name={"ReportTitle"}
                                label={t("schedule.report")}
                                component={FormInput}
                                maxLength={255}
                                disabled
                              />
                              {pageState.formValues.Widgets?.length > 0 && (
                                <Field
                                  id={"WidgetTitle"}
                                  name={"WidgetTitle"}
                                  label={t("extras.widget")}
                                  component={FormInput}
                                  disabled
                                />
                              )}
                            </>
                          )}

                          {pageState.formValues.importexport == "import" && (
                            <>
                              <Field
                                id={"DatasetId"}
                                name={"DatasetId"}
                                label={t("datasources.dataset")}
                                component={FormDropDownListById}
                                data={lstOfDatasets}
                                dataItemKey="Id"
                                textField="Title"
                                value={pageState.formValues.DatasetId}
                                disabled
                              />
                            </>
                          )}

                          <Field
                            id={"Name"}
                            name={"Name"}
                            label={t("schedule.name")}
                            component={FormInput}
                            maxLength={255}
                            onChange={(e) => {
                              props.onChangeFormValue("Name", e.value);
                            }}
                          />
                          <Field
                            id={"Description"}
                            name={"Description"}
                            label={t("schedule.description")}
                            component={FormTextArea}
                            style={{ height: 80 }}
                            onChange={(e) => {
                              props.onChangeFormValue("Description", e.value);
                            }}
                          />

                          <Field
                            id={"Type"}
                            name={"TypeId"}
                            label={t("schedule.type")}
                            component={FormDropDownListById}
                            data={ddlType}
                            dataItemKey="Id"
                            textField="Title"
                            onChange={(e) => {
                              props.onChangeFormValue("TypeId", e.value);
                            }}
                          />

                          <Field
                            id={"Source"}
                            name={"SourceId"}
                            label={t("schedule.source")}
                            component={FormDropDownListById}
                            data={ddlSource}
                            dataItemKey="Id"
                            textField="Title"
                            onChange={(e) => {
                              props.onChangeFormValue("SourceId", e.value);
                            }}
                          />

                          {pageState.formValues.importexport == "import" && (
                            <>
                              <Field
                                id={"IdentityColumn"}
                                name={"IdentityColumn"}
                                label={t("schedule.IdentityColumn")}
                                component={FormInput}
                                disabled
                              />
                            </>
                          )}

                          {pageState.formValues.ShowGoogleSheets && (
                            <>
                              <Field
                                id={"Connections"}
                                name={"ConnectorConfigId"}
                                label={t("connection.connection")}
                                component={FormDropDownListById}
                                data={pageState.formValues.Connections}
                                dataItemKey="ConnectionId"
                                textField="ConnectionTitle"
                                value={pageState.formValues.ConnectorConfigId}
                                onChange={(e) => {
                                  props.onChangeFormValue(
                                    "ConnectorConfigId",
                                    e.value,
                                  );
                                }}
                              />
                              {pageState.formValues.ConnectorConfigId && (
                                <>
                                  <Field
                                    id={"File"}
                                    name={"FileId"}
                                    label={t("schedule.file")}
                                    component={FormDropDownListById}
                                    data={listOfFiles}
                                    dataItemKey="SheetId"
                                    textField="FilePath"
                                    value={pageState.formValues.FileId}
                                    onChange={(e) => {
                                      const file = listOfFiles.find(
                                        (f) => f.SheetId == e.value,
                                      );
                                      props.onChangeFormValue("File", e.value);
                                      props.onChangeFormValue(
                                        "SheetId",
                                        e.value,
                                      );
                                      props.onChangeFormValue(
                                        "FilePath",
                                        file.FilePath,
                                      );
                                      // props.onChangeFormValue("SheetName", "");
                                    }}
                                  />

                                  {pageState.formValues.FileId && (
                                    <Field
                                      id="SheetName"
                                      name="SheetName"
                                      label={t("schedule.sheetname")}
                                      component={FormDropDownList}
                                      data={
                                        pageState.formValues.GoogleList?.find(
                                          (e) =>
                                            e.SheetId ===
                                            pageState.formValues.FileId,
                                        )?.SheetNames || []
                                      }
                                      value={pageState.formValues.SheetName}
                                      onChange={(e) =>
                                        props.onChangeFormValue(
                                          "SheetName",
                                          e.value,
                                        )
                                      }
                                    />
                                  )}

                                  {pageState.formValues.importexport ==
                                    "export" &&
                                    pageState.formValues.GoogleSheetId > 0 && (
                                      <>
                                        {/* Add */}
                                        <button
                                          type="button"
                                          onClick={addCustomField}
                                          style={{
                                            padding: "6px 20px",
                                            color: currentTheme.touchColor,
                                            fontWeight: 500,
                                            marginTop: "20px",
                                            border: `1px solid rgba(0, 0, 0, 0.06)`,
                                            borderRadius: "5px",
                                            width: "fit-content",
                                            float: "right",
                                          }}
                                        >
                                          + {t("schedule.add")}
                                        </button>

                                        <div
                                          className="scheduleview-custom-fields"
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 12,
                                            width: "100%",
                                          }}
                                        >
                                          {customFields.map((item, index) => (
                                            <div
                                              key={index}
                                              className="scheduleview-custom-field-row"
                                              style={{
                                                display: "flex",
                                                gap: 12,
                                                width: "100%",
                                                alignItems: "flex-end",
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: 30,
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {item.index}.
                                              </div>
                                              <Field
                                                name={`CustomFields[${index}].field`}
                                                label={t(
                                                  "schedule.customField",
                                                )}
                                                //defaultValue={item.field}
                                                component={FormDropDownList}
                                                data={
                                                  pageState.formValues
                                                    .ColumnNames
                                                }
                                                style={{
                                                  flex: 1,
                                                  width: "350px",
                                                  height: "34px",
                                                }}
                                                onChange={(e) =>
                                                  updateCustomField(
                                                    index,
                                                    "field",
                                                    e.value,
                                                  )
                                                }
                                              />

                                              <Field
                                                name={`CustomFields[${index}].value`}
                                                label={t(
                                                  "schedule.customFieldValue",
                                                )}
                                                component={FormInput}
                                                maxLength={255}
                                                //defaultValue={item.value}
                                                style={{
                                                  flex: 1,
                                                  width: "350px",
                                                  marginTop: "0",
                                                }}
                                                onChange={(e) =>
                                                  updateCustomField(
                                                    index,
                                                    "value",
                                                    e.value,
                                                  )
                                                }
                                              />

                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removeCustomField(index)
                                                }
                                                style={{
                                                  height: 34,
                                                  alignSelf: "flex-end",
                                                  border: "none",
                                                  background: "transparent",
                                                  cursor: "pointer",
                                                }}
                                              >
                                                ❌
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                </>
                              )}
                            </>
                          )}
                        </CollapsableFormRegion>
                        <CollapsableFormRegion
                          className="form-region-main"
                          title={t("alerts.recurrence")}
                          open={pageState.formValues.recurrsOpen}
                          onToggle={(e) =>
                            dispatch(
                              setFormValue({
                                id: "recurrsOpen",
                                value: !pageState.formValues.recurrsOpen,
                              }),
                            )
                          }
                        >
                          <Field
                            id={"RecurrencyFrom"}
                            name={"RecurrencyFrom"}
                            label={t("alerts.recurs")}
                            component={FormRadioGroup}
                            data={selectFromList}
                            value={pageState.formValues.RecurrencyFrom}
                            onChange={props.onRecurrencyOptionChange}
                          />
                          {pageState.formValues.RecurrencyFrom ==
                            "fromRecurrency" && (
                            <Field
                              id={"RecursEvery"}
                              name={"RecursEvery"}
                              label={t("alerts.recurs")}
                              component={FormDropDownListById}
                              data={ddlRecurrsEvery}
                              dataItemKey="Id"
                              textField="Title"
                              onChange={(e) =>
                                props.onChangeFormValue("RecursEvery", e.value)
                              }
                            />
                          )}
                          {pageState.formValues.RecurrencyFrom ==
                            "fromCron" && (
                            <>
                              <Field
                                id={"CronValue"}
                                name={"CronValue"}
                                label={t("schedule.CronValue")}
                                disabled
                                component={FormInput}
                                style={{ marginBottom: "20px" }}
                              />

                              <ReQuartzCron
                                value={pageState.formValues.CronValue}
                                onChange={(e) =>
                                  props.onChangeFormValue("CronValue", e)
                                }
                              />
                            </>
                          )}
                        </CollapsableFormRegion>
                        {pageState.formValues.importexport == "export" && (
                          <CollapsableFormRegion
                            className="form-region-filter"
                            title={t("alerts.filter1")}
                            open={pageState.formValues.filterOpen}
                            onToggle={(e) =>
                              dispatch(
                                setFormValue({
                                  id: "filterOpen",
                                  value: !pageState.formValues.filterOpen,
                                }),
                              )
                            }
                          >
                            <div className="form-group form-group-1">
                              <QueryBuilder
                                query={pageState.formValues.WhereObject}
                                fields={pageState.currentDataset.Where}
                                getOperators={getOperators}
                                onQueryChange={(q) => {
                                  dispatch(
                                    setFormValue({
                                      id: "WhereObject",
                                      value: q,
                                    }),
                                  );
                                }}
                                controlElements={{
                                  valueEditor: (editorProps) => (
                                    <QueryValueEditor
                                      {...editorProps}
                                      handleSaveKey={props.onKeyChange}
                                    />
                                  ),
                                }}
                                showCloneButtons={true}
                              />
                            </div>
                          </CollapsableFormRegion>
                        )}
                      </div>
                    </TabStripTab>
                  </TabStrip>
                </div>
              </FormRegion>
            </FormElement>
          );
        }}
      />
      <style>
        {`        
                    .scheduleview-page-form .k-form
                    {          
                        /*the kendo form which is child of my form.content*/     
                        width: 100%;
                        display: flex;
                        flex: 1;
                        justify-content: space-between;
                        padding: 0 24px 12px 24px;/*keep 12px in right and bottom so that when minimizing we keep a white region to the right and bottom; also keep 8px on the left*/
                    }

                    .scheduleview-page-form .form-region-1
                    {
                        width: 100%;
                        max-width: 1000px;
                        margin-top: 20px !important;/*to override kendo first child of k-form having marginTop=0*/                     
                    }

                    .scheduleview-page-form .form-region-container{
                        margin-bottom:30px;
                    }

                    .scheduleview-page-form .form-region-1 .form-group-1
                    {
                        display: flex;
                        flex-direction: column;
                        width: 100%;
                    }
                   
                    .scheduleview-page-form > .content
                    {
                        padding: 0px;
                        background-color: transparent;
                        align-items: flex-start;
                    }

                    .scheduleview-page-form .form-region-root
                    {
                        background-color: transparent;
                    }

                    .scheduleview-page-form input.k-input, .scheduleview-page-form textarea.k-textarea, 
                    .scheduleview-page-form .k-dropdownlist .k-input-inner
                    {
                        /*make input controls have a bit more height*/
                        padding: 6px 8px;
                    }

                    .scheduleview-page-form .form-region-root .list-container.groups
                    {
                        border: 1px solid ${currentTheme.componentBorderColor};
                        border-radius: 4px;
                        margin-top: 2px;
                        flex: 1;
                        max-height: 180px;
                    }

                    .scheduleview-page-form .form-region-root .list-container.groups .list-root
                    {
                        display: flex !important;
                        flex-wrap: wrap;   
                    }

                    .scheduleview-page-form .form-region-root .list-container.groups .list-item
                    {
                        display: flex !important;
                        width: 50%;
                        overflow-x: hidden;
                    }

                   .scheduleview-page-form .k-animation-container.k-animation-container-relative{
                    width:100%;
                   }

                   .scheduleview-page-form .toolbar .k-button{
                   border:none;
                   }
 
                     .scheduleview-page-form .ruleGroup-addGroup
                      {
                        display:none;
                      }

                      .cron-option-label {
                        display: inline-flex;
                        align-items: center;
                        gap: 6px;
                      }

                      .cron-help-icon {
                        cursor: pointer;
                        font-size: 11px;
                        font-weight: bold;
                        border: 1px solid #000;
                        border-radius: 50%;
                        width: 15px;
                        height: 15px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        user-select: none;
                        line-height: 14px;
                      }

                       /*filter section*/
                      .scheduleview-page-form .form-region-filter
                      {
                          margin-top: 36px!important;
                          border-radius: 4px;*/
                          padding: 8px 0px 8px 0px;
                      }

                      .scheduleview-page-form .form-region-filter .header
                      {
                          padding: 4px;
                          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
                      }

                      .scheduleview-page-form .form-region-filter .header .right-line
                      {
                          display: none;
                      }

                      .scheduleview-page-form .form-region-filter .content
                      {
                          padding-left: 12px;
                          margin-top: 12px;
                      }
                      
                      

                      /*query builder*/
                      .scheduleview-page-form .form-region-filter .ruleGroup, .scheduleview-page-form .form-region-filter .ruleGroup-combinators,
                      .scheduleview-page-form .form-region-filter .ruleGroup-addRule, .scheduleview-page-form .form-region-filter .ruleGroup-addGroup,
                      .scheduleview-page-form .form-region-filter .rule-remove, .scheduleview-page-form .form-region-filter .ruleGroup-remove,
                      .scheduleview-page-form .form-region-filter .rule-fields, .scheduleview-page-form .form-region-filter .rule-operators,
                      .scheduleview-page-form .form-region-filter .rule-value, .scheduleview-page-form .form-region-filter .rule-cloneRule
                      {
                          border-color: rgba(0, 0, 0, 0.06);
                          background-color: #fff;
                          color: #424242;                                               
                      }

                      .scheduleview-page-form .form-region-filter .rule-fields:focus, .scheduleview-page-form .form-region-filter .rule-operators:focus,
                      .scheduleview-page-form .form-region-filter .rule-value:focus
                      {
                          outline: none
                      }    

                      .scheduleview-page-form .form-region-filter .queryBuilder .rule
                      {
                          display: flex;
                          align-items: center;
                          margin-bottom: 8px;
                      }
                      
                      .scheduleview-page-form .form-region-filter .rule-remove, .scheduleview-page-form .form-region-filter .ruleGroup-remove
                      {
                          color: #d51923;
                          padding: 8px 10px 7px 10px;
                      }

                     .scheduleview-page-form .form-region-filter .rule-fields
                      {
                          padding: 6px 4px;
                          border-radius:4px;
                          margin-right: 6px;
                          max-width: 275px;
                      }
  
                      .scheduleview-page-form .form-region-filter .ruleGroup{
                          background: none;
                          margin-top: 4px;
                      }

                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup[data-level="1"],
                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup[data-level="2"],
                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup[data-level="3"],
                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup[data-level="4"],
                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup[data-level="5"]
                    {
                        margin-top: 18px;
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup-body{
                        margin-top: 8px;
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .rule-operators
                    {
                        padding:7px 4px 6px 4px;
                        border-radius:4px;
                        width: 90px;
                        margin-right: 6px;
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .rule-value
                    {
                        padding:7px 4px 6px 4px;
                        border-radius:4px;
                        border-width: 1px;
                        flex:1;
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup-combinators
                    {
                        padding: 8px;
                        border-radius: 4px;
                        border-width:1px;
                        cursor: pointer;  
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup-addRule{
                        margin-left: 8px;
                        padding: 8px;
                        border-radius: 4px;
                        border-width:1px;
                        cursor: pointer; 
                    }

                    .scheduleview-page-form .form-region-filter .queryBuilder .ruleGroup-addGroup{
                        margin-left: 8px;
                        padding: 8px;
                        border-radius: 4px;
                        border-width:1px;
                        cursor: pointer; 
                        display: none !important;
                    }

                    .scheduleview-page-form .form-region-filter .rule-remove,        
                    .scheduleview-page-form .form-region-filter .ruleGroup-remove,
                    .scheduleview-page-form .form-region-filter .rule-cloneRule
                    {
                        /* background-color: #0096fb; */
                        /*color: black;*/
                        font-weight: 700;
                        border-radius: 4px;
                        border-width: 1px;
                        padding: 6px 8px;
                        margin-left: 8px;
                        cursor: pointer; 
                    }

                    @media (max-width: 1024px) {
                      .scheduleview-page-form .k-form {
                        box-sizing: border-box;
                        overflow-x: hidden;
                        padding: 0 16px 16px;
                      }

                      .scheduleview-page-form .form-region-1 {
                        margin-top: 12px !important;
                      }

                      .scheduleview-page-form .form-region-root .list-container.groups .list-item {
                        width: 100%;
                      }

                      .scheduleview-page-form .form-region-filter .content {
                        padding-left: 0;
                        padding-right: 0;
                      }

                      .scheduleview-page-form .form-region-filter .queryBuilder .rule {
                        align-items: stretch;
                        flex-wrap: wrap;
                        gap: 8px;
                      }

                      .scheduleview-page-form .form-region-filter .rule-fields,
                      .scheduleview-page-form .form-region-filter .queryBuilder .rule-operators,
                      .scheduleview-page-form .form-region-filter .queryBuilder .rule-value {
                        box-sizing: border-box;
                        flex: 1 1 100%;
                        margin-right: 0;
                        max-width: none;
                        width: 100%;
                      }

                      .scheduleview-page-form .form-region-filter .rule-remove,
                      .scheduleview-page-form .form-region-filter .rule-cloneRule {
                        margin-left: 0;
                      }

                      .scheduleview-page-form .form-region-filter .ruleGroup-addRule {
                        margin-left: 0;
                      }

                      .scheduleview-page-form .scheduleview-custom-field-row {
                        align-items: stretch !important;
                        flex-wrap: wrap;
                      }

                      .scheduleview-page-form .scheduleview-custom-field-row > div:first-child {
                        flex: 0 0 100%;
                      }

                      .scheduleview-page-form .scheduleview-custom-field-row .k-form-field {
                        flex: 1 1 100% !important;
                        width: 100% !important;
                      }

                      .scheduleview-page-form .scheduleview-custom-field-row > button {
                        align-self: flex-start !important;
                      }
                    }
                `}
      </style>
    </Fragment>
  );
};
